import { Box, IconButton, Chip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CTextField } from "@/components/TextField/CTextField";
import type { AdditionalFieldItem } from "@/features/accountSettings/types/additionalFields";
import { z } from "zod";
import { getFieldTypeLabel } from "../constants";
import { validateFieldDuplication } from "../utils/fieldValidation";

/**
 * 項目名編集用のZodスキーマ
 */
const EditFieldSchema = z.object({
  label: z
    .string()
    .min(1, "項目名を入力してください")
    .max(20, "項目名は20文字以内で入力してください"),
});

type EditFieldFormData = z.infer<typeof EditFieldSchema>;

interface FieldItemProps {
  field: AdditionalFieldItem;
  isEditing: boolean;
  allFields: AdditionalFieldItem[] | undefined;
  onEdit: (fieldId: string, currentKey: string) => void;
  onSave: (fieldId: string, newKey: string) => void;
  onCancel: () => void;
  onDelete: () => void;
}

/**
 * 個別フィールドアイテムコンポーネント
 * 通常表示モードと編集モードを切り替えて表示
 */
export const FieldItem: React.FC<FieldItemProps> = ({
  field,
  isEditing,
  allFields,
  onEdit,
  onSave,
  onCancel,
  onDelete,
}) => {
  const fieldTypeLabel = getFieldTypeLabel(field.fieldType);

  // 編集用フォーム
  const editForm = useForm<EditFieldFormData>({
    mode: "all",
    defaultValues: {
      label: field.key,
    },
    resolver: zodResolver(EditFieldSchema),
  });

  // 編集開始時にフォームを初期化
  React.useEffect(() => {
    if (isEditing) {
      editForm.reset({ label: field.key });
    }
  }, [isEditing, field.key, editForm]);

  const handleSave = editForm.handleSubmit((data: EditFieldFormData) => {
    // 重複バリデーション
    const validationResult = validateFieldDuplication(
      allFields,
      data.label,
      field.fieldType,
      field.id
    );

    if (!validationResult.isValid) {
      editForm.setError("label", {
        type: "manual",
        message: validationResult.errorMessage,
      });
      return;
    }

    onSave(field.id, data.label);
  });

  return (
    <Box
      sx={{
        mb: 1,
        display: "inline-flex",
        alignItems: isEditing ? "flex-start" : "center",
        gap: 1,
        p: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        mr: 1,
      }}
    >
      {isEditing ? (
        // 編集モード
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ width: 200 }}>
              <CTextField
                control={editForm.control}
                name="label"
                label="項目名"
                maxLength={20}
              />
            </Box>
            <Chip
              label={`型: ${fieldTypeLabel}`}
              size="small"
              sx={{ alignSelf: "flex-start" }}
            />
          </Box>
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              void handleSave();
            }}
            aria-label="保存"
          >
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onCancel} aria-label="キャンセル">
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        // 通常モード
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span>{field.key}</span>
            <Chip label={fieldTypeLabel} size="small" />
          </Box>
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              onEdit(field.id, field.key);
            }}
            aria-label={`${field.key}を編集`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={onDelete}
            aria-label={`${field.key}を削除`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      )}
    </Box>
  );
};
